import { useEffect, useRef, useState } from "react";
import { Copy, ImageIcon, Loader2, Pencil, Plus, Scissors, Tag, Upload, X } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { cn } from "@/lib/utils";
import type { CatalogUnit, ClientType, CutType, GlutenStatus, Product, ProductLabelInfo, SegmentPriceInput, Unit } from "@/lib/types";
import type { CatalogUnitInput, CatalogUnitUsage } from "@/lib/catalogApi";
import { CLIENT_TYPE_LABELS } from "@/lib/clientTypeLabels";
import { UnitDialog } from "@/components/admin/UnitDialog";
import { ProductCostPanel } from "@/components/admin/ProductCostPanel";

/** Ordem fixa das colunas de unidade; grupo desconhecido/ausente cai em "OUTRAS" (sempre por último). */
const UNIT_GROUP_ORDER = ["LITROS", "BANDEJA", "KG", "PACOTE", "UNIDADE"] as const;
const UNIT_GROUP_OUTRAS = "OUTRAS";

export type DraftProduct = Omit<Product, "id"> & { id?: string };

export const emptyLabelInfo: ProductLabelInfo = {
  hasBarcode: false,
  ean: "",
  netWeightGrams: undefined,
  shelfLifeDays: undefined,
  portionSizeGrams: undefined,
  storageText: "",
  glutenStatus: "NAO_CONTEM",
  energyKcal: undefined,
  carbsG: undefined,
  sugarsTotalG: undefined,
  sugarsAddedG: undefined,
  proteinG: undefined,
  fatTotalG: undefined,
  fatSaturatedG: undefined,
  fatTransG: undefined,
  fiberG: undefined,
  sodiumMg: undefined,
};

export const emptyDraft: DraftProduct = {
  name: "",
  category: "FRUTAS",
  imageUrl: "",
  defaultUnit: "Kg",
  availableUnits: ["Kg"],
  isProcessed: false,
  active: true,
  availableCuts: [],
  segments: [],
  wastePercent: undefined,
  labelInfo: emptyLabelInfo,
};

const NUTRITION_FIELDS: { key: keyof ProductLabelInfo; label: string }[] = [
  { key: "energyKcal", label: "Valor energético (kcal)" },
  { key: "carbsG", label: "Carboidratos (g)" },
  { key: "sugarsTotalG", label: "Açúcares totais (g)" },
  { key: "sugarsAddedG", label: "Açúcares adicionados (g)" },
  { key: "proteinG", label: "Proteínas (g)" },
  { key: "fatTotalG", label: "Gorduras totais (g)" },
  { key: "fatSaturatedG", label: "Gorduras saturadas (g)" },
  { key: "fatTransG", label: "Gorduras trans (g)" },
  { key: "fiberG", label: "Fibra alimentar (g)" },
  { key: "sodiumMg", label: "Sódio (mg)" },
];

const clamp = (value: number, min = 0, max = 100) => Math.min(max, Math.max(min, value));
const formatPercent = (value: number) => (Number.isInteger(value) ? value.toString() : value.toFixed(2).replace(/\.?0+$/, ""));

export function ProductThumb({
  imageUrl,
  name,
  fallback,
  className,
}: {
  imageUrl?: string;
  name: string;
  fallback?: string;
  className?: string;
}) {
  if (imageUrl) {
    return <img src={imageUrl} alt={name} className={cn("rounded-lg bg-secondary object-cover", className)} />;
  }
  if (fallback) {
    return (
      <div className={cn("flex items-center justify-center rounded-lg bg-secondary", className)}>
        <span className="text-xl">{fallback}</span>
      </div>
    );
  }
  return (
    <div className={cn("flex items-center justify-center rounded-lg bg-secondary text-muted-foreground", className)}>
      <ImageIcon className="h-4 w-4" />
    </div>
  );
}

export interface ProductDialogProps {
  open: boolean;
  loading: boolean;
  draft: DraftProduct | null;
  categoryOptions: { id: string; label: string; emoji: string }[];
  units: CatalogUnit[];
  cutOptions: string[];
  onClose: () => void;
  onSave: (data: Omit<Product, "id">) => Promise<void>;
  /** Habilita os botões de adicionar/editar unidade (ADMIN/PLANEJADOR). */
  canManageUnits?: boolean;
  /** Exibe a seção "Custos" (permissão CATALOGO.CUSTO.VISUALIZAR) — só em produto já existente. */
  canViewCost?: boolean;
  /** Habilita a edição na seção "Custos" (permissão CATALOGO.CUSTO.EDITAR). */
  canEditCost?: boolean;
  onCreateUnit?: (input: CatalogUnitInput) => Promise<CatalogUnit>;
  onUpdateUnit?: (id: string, input: CatalogUnitInput) => Promise<CatalogUnit>;
  onFetchUnitUsage?: (id: string) => Promise<CatalogUnitUsage>;
}

export function ProductDialog({
  open,
  loading,
  draft,
  categoryOptions,
  units,
  cutOptions,
  onClose,
  onSave,
  canManageUnits = false,
  canViewCost = false,
  canEditCost = false,
  onCreateUnit,
  onUpdateUnit,
  onFetchUnitUsage,
}: ProductDialogProps) {
  const [unitDialog, setUnitDialog] = useState<{ open: boolean; mode: "create" | "edit" }>({
    open: false,
    mode: "create",
  });
  const [form, setForm] = useState<DraftProduct>(emptyDraft);
  // Preços por célula segmento||unidade||corte. No create parte vazio; na edição é semeado com os
  // preços reais vigentes do produto e reenviado no submit (create e update fazem upsert no backend).
  const [segmentPrices, setSegmentPrices] = useState<Record<string, string>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;

    const fallbackUnit = units.find((unit) => unit.active)?.name ?? units[0]?.name ?? "Kg";
    const fallbackCategory = categoryOptions.find((category) => category.id !== "ALL")?.id ?? "FRUTAS";
    const fallbackCut = cutOptions[0];

    if (draft) {
      // Pré-preenche a grade com os preços reais vigentes do produto (edição). Mesma chave
      // segmento||unidade||corte usada no submit; placeholders (0,00) já não vêm do backend.
      const seeded: Record<string, string> = {};
      for (const price of draft.segmentPrices ?? []) {
        seeded[`${price.segment}||${price.unit}||${price.cut ?? ""}`] = String(price.price);
      }
      setSegmentPrices(seeded);
      setForm({
        ...emptyDraft,
        ...draft,
        category: draft.category || fallbackCategory,
        defaultUnit: draft.defaultUnit || fallbackUnit,
        availableUnits:
          draft.availableUnits && draft.availableUnits.length > 0
            ? draft.availableUnits
            : [draft.defaultUnit || fallbackUnit],
        // Corte só existe para produtos processados; in natura nunca carrega corte.
        availableCuts:
          draft.category !== "PROCESSADOS"
            ? []
            : draft.availableCuts && draft.availableCuts.length > 0
              ? draft.availableCuts
              : fallbackCut
                ? [fallbackCut]
                : [],
      });
      return;
    }

    setSegmentPrices({});
    setForm({
      ...emptyDraft,
      category: fallbackCategory,
      defaultUnit: fallbackUnit,
      availableUnits: [fallbackUnit],
    });
  }, [open, draft, categoryOptions, units, cutOptions]);

  const update = <K extends keyof DraftProduct>(key: K, value: DraftProduct[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  const isProcessedCategory = form.category === "PROCESSADOS";

  // Troca de categoria mantém os cortes só quando o destino é PROCESSADOS; caso
  // contrário limpa a seleção (in natura não tem corte).
  const setCategory = (value: string) =>
    setForm((current) => ({
      ...current,
      category: value,
      availableCuts: value === "PROCESSADOS" ? current.availableCuts : [],
    }));

  const priceUnits: Unit[] =
    form.availableUnits && form.availableUnits.length > 0 ? form.availableUnits : [form.defaultUnit];
  // Colunas de corte da grade: processados usam os cortes escolhidos; in natura tem só o preço base.
  const priceCuts: (CutType | null)[] =
    isProcessedCategory && (form.availableCuts?.length ?? 0) > 0 ? form.availableCuts! : [null];
  const priceKey = (segment: ClientType, unit: Unit, cut: CutType | null) =>
    `${segment}||${unit}||${cut ?? ""}`;

  const copyPricesToOthers = (source: ClientType) =>
    setSegmentPrices((current) => {
      const next = { ...current };
      for (const target of form.segments ?? []) {
        if (target === source) continue;
        for (const unit of priceUnits) {
          for (const cut of priceCuts) {
            next[priceKey(target, unit, cut)] = current[priceKey(source, unit, cut)] ?? "";
          }
        }
      }
      return next;
    });

  const label = form.labelInfo ?? emptyLabelInfo;
  const updateLabel = <K extends keyof ProductLabelInfo>(key: K, value: ProductLabelInfo[K]) =>
    setForm((current) => ({
      ...current,
      labelInfo: { ...(current.labelInfo ?? emptyLabelInfo), [key]: value },
    }));
  const updateLabelNumber = (key: keyof ProductLabelInfo, raw: string) =>
    updateLabel(key, (raw === "" ? undefined : Number(raw)) as ProductLabelInfo[typeof key]);

  const toggleCut = (cut: CutType) => {
    if (!isProcessedCategory) return; // corte só é selecionável em PROCESSADOS
    const currentCuts = form.availableCuts ?? [];
    update("availableCuts", currentCuts.includes(cut) ? currentCuts.filter((item) => item !== cut) : [...currentCuts, cut]);
  };

  const toggleSegment = (segment: ClientType) => {
    const current = form.segments ?? [];
    update("segments", current.includes(segment) ? current.filter((item) => item !== segment) : [...current, segment]);
  };

  const toggleUnit = (unit: Unit) => {
    const currentUnits = form.availableUnits ?? [];
    const nextUnits = currentUnits.includes(unit) ? currentUnits.filter((item) => item !== unit) : [...currentUnits, unit];
    update("availableUnits", nextUnits);
    if (!nextUnits.includes(form.defaultUnit) && nextUnits.length > 0) {
      update("defaultUnit", nextUnits[0]);
    }
  };

  const selectedUnitNames = form.availableUnits ?? [];
  // Unidade padrão só oferece unidades ativas (mais as já selecionadas no produto).
  const activeUnitNames = units.filter((unit) => unit.active).map((unit) => unit.name);
  const groupOf = (unit: CatalogUnit) =>
    unit.group && (UNIT_GROUP_ORDER as readonly string[]).includes(unit.group) ? unit.group : UNIT_GROUP_OUTRAS;
  // Só oferecemos unidades ativas para nova seleção; inativas aparecem apenas se o produto já as usa.
  const visibleUnits = units.filter((unit) => unit.active || selectedUnitNames.includes(unit.name));
  const unitsByGroup = new Map<string, CatalogUnit[]>();
  for (const unit of visibleUnits) {
    const group = groupOf(unit);
    const bucket = unitsByGroup.get(group);
    if (bucket) bucket.push(unit);
    else unitsByGroup.set(group, [unit]);
  }
  const orderedGroups = [...UNIT_GROUP_ORDER, UNIT_GROUP_OUTRAS].filter((group) => unitsByGroup.has(group));

  const canManage = canManageUnits && !!onCreateUnit && !!onUpdateUnit && !!onFetchUnitUsage;

  // Cria a unidade via callback e já a deixa selecionada no produto (quando ativa), sem recarregar a tela.
  const handleCreateUnit = async (input: CatalogUnitInput): Promise<CatalogUnit> => {
    const created = await onCreateUnit!(input);
    if (created.active && !selectedUnitNames.includes(created.name)) {
      update("availableUnits", [...selectedUnitNames, created.name]);
    }
    return created;
  };

  const onFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Selecione um arquivo de imagem");
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error("Imagem muito grande (máx. 2MB)");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update("imageUrl", reader.result as string);
    reader.readAsDataURL(file);
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Informe o nome do produto");
      return;
    }

    const units = form.availableUnits && form.availableUnits.length > 0 ? form.availableUnits : [form.defaultUnit];
    if (!units.includes(form.defaultUnit)) {
      toast.error("A unidade padrão deve estar nas unidades disponíveis");
      return;
    }

    const labelInfo = form.labelInfo ?? emptyLabelInfo;
    if (labelInfo.hasBarcode && (!labelInfo.ean || labelInfo.ean.trim() === "")) {
      toast.error("Informe o número do código de barras (EAN)");
      return;
    }

    // Monta os preços informados por segmento × unidade × corte (só células com preço > 0). Células em
    // branco são omitidas: o backend grava placeholder e o produto fica oculto até ser precificado.
    const parsedSegmentPrices: SegmentPriceInput[] = [];
    for (const segment of form.segments ?? []) {
      for (const unit of units) {
        for (const cut of priceCuts) {
          const raw = segmentPrices[priceKey(segment, unit, cut)];
          const value = raw ? parseFloat(raw.replace(",", ".")) : NaN;
          if (Number.isFinite(value) && value > 0) {
            parsedSegmentPrices.push({ segment, unit, cut: cut ?? null, price: +value.toFixed(2) });
          }
        }
      }
    }

    const { id: _id, ...rest } = form;
    await onSave({
      ...rest,
      name: form.name.trim(),
      imageUrl: form.imageUrl?.trim() || undefined,
      availableUnits: units,
      availableCuts: form.availableCuts && form.availableCuts.length > 0 ? form.availableCuts : undefined,
      wastePercent: form.wastePercent != null ? +form.wastePercent.toFixed(2) : undefined,
      labelInfo: {
        ...labelInfo,
        ean: labelInfo.ean?.trim() || undefined,
        storageText: labelInfo.storageText?.trim() || undefined,
      },
      segmentPrices: parsedSegmentPrices.length > 0 ? parsedSegmentPrices : undefined,
    });
  };

  return (
    <>
    <Dialog open={open} onOpenChange={(nextOpen) => !nextOpen && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-lg overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl">
            {draft?.id ? "Editar produto" : "Novo produto"}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="rounded-lg border border-border bg-secondary/20 p-8 text-center text-sm text-muted-foreground">
            <Loader2 className="mx-auto mb-3 h-5 w-5 animate-spin text-primary" />
            Carregando produto...
          </div>
        )}

        {!loading && (
          <>
            <div className="space-y-4 text-sm">
              <div className="space-y-2">
                <Label>Imagem do produto</Label>
                <div className="flex items-start gap-3">
                  <div className="relative">
                    <ProductThumb imageUrl={form.imageUrl} name={form.name || "Produto"} className="h-24 w-24 border border-border" />
                    {form.imageUrl && (
                      <button
                        type="button"
                        onClick={() => update("imageUrl", "")}
                        className="absolute -right-2 -top-2 flex h-6 w-6 items-center justify-center rounded-full bg-destructive text-destructive-foreground shadow-md hover:opacity-90"
                        aria-label="Remover imagem"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    )}
                  </div>
                  <div className="flex-1 space-y-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(event) => {
                        const file = event.target.files?.[0];
                        if (file) {
                          void onFile(file);
                        }
                        event.target.value = "";
                      }}
                    />
                    <Button type="button" variant="outline" size="sm" onClick={() => fileInputRef.current?.click()} className="w-full">
                      <Upload className="h-4 w-4" /> Fazer upload
                    </Button>
                    <Input
                      value={form.imageUrl ?? ""}
                      onChange={(event) => update("imageUrl", event.target.value)}
                      placeholder="ou cole uma URL"
                    />
                    <p className="text-xs text-muted-foreground">PNG/JPG até 2MB.</p>
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Nome *</Label>
                <Input value={form.name} onChange={(event) => update("name", event.target.value)} placeholder="Ex.: Tomate Italiano" />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Categoria *</Label>
                  <Select value={form.category} onValueChange={setCategory}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {categoryOptions.filter((category) => category.id !== "ALL").map((category) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.emoji} {category.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Status</Label>
                  <Select
                    value={form.active === false ? "inactive" : "active"}
                    onValueChange={(value) => update("active", value === "active")}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">Ativo</SelectItem>
                      <SelectItem value="inactive">Inativo</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <Label className="m-0">Unidades disponíveis *</Label>
                  {canManage && (
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setUnitDialog({ open: true, mode: "create" })}
                      >
                        <Plus className="h-4 w-4" /> Adicionar unidade
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setUnitDialog({ open: true, mode: "edit" })}
                      >
                        <Pencil className="h-4 w-4" /> Editar unidade
                      </Button>
                    </div>
                  )}
                </div>
                {orderedGroups.length === 0 ? (
                  <p className="rounded-lg border border-dashed border-border bg-secondary/20 p-3 text-xs text-muted-foreground">
                    Nenhuma unidade disponível.{canManage ? " Use “Adicionar unidade” para criar a primeira." : ""}
                  </p>
                ) : (
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                    {orderedGroups.map((group) => (
                      <div key={group} className="rounded-lg border border-border bg-secondary/20 p-2">
                        <p className="mb-2 text-center text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                          {group}
                        </p>
                        <div className="flex flex-col gap-1.5">
                          {[...unitsByGroup.get(group)!]
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map((unit) => {
                              const active = selectedUnitNames.includes(unit.name);
                              return (
                                <Button
                                  key={unit.id}
                                  type="button"
                                  variant="chip"
                                  size="sm"
                                  data-active={active}
                                  className="w-full justify-center"
                                  onClick={() => toggleUnit(unit.name)}
                                  title={unit.active ? undefined : "Unidade inativa — mantida por já estar em uso"}
                                >
                                  {unit.name}{unit.active ? "" : " (inativa)"}
                                </Button>
                              );
                            })}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="space-y-2">
                <Label>Unidade padrão *</Label>
                <Select value={form.defaultUnit} onValueChange={(value) => update("defaultUnit", value as Unit)}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(form.availableUnits && form.availableUnits.length > 0 ? form.availableUnits : activeUnitNames).map((unit) => (
                      <SelectItem key={unit} value={unit}>{unit}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">Unidade pré-selecionada quando o cliente adiciona ao carrinho.</p>
              </div>

              {/* "Processado" é definido pela categoria PROCESSADOS — este atalho marca/desmarca
                  essa categoria, ficando em sincronia com o seletor de Categoria acima. */}
              <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-secondary/40 px-3 py-2.5">
                <input
                  type="checkbox"
                  checked={form.category === "PROCESSADOS"}
                  onChange={(event) =>
                    setCategory(
                      event.target.checked
                        ? "PROCESSADOS"
                        : categoryOptions.find((c) => c.id !== "ALL" && c.id !== "PROCESSADOS")?.id ?? "FRUTAS",
                    )
                  }
                  className="h-4 w-4 accent-primary"
                />
                <span className="text-sm">Produto processado (categoria PROCESSADOS)</span>
              </label>

              <div className="space-y-2 rounded-lg border border-border bg-secondary/30 p-3">
                <div className="flex items-center gap-2">
                  <Scissors className="h-4 w-4 text-muted-foreground" />
                  <Label className="m-0">Merma / desperdício no preparo</Label>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs font-normal text-muted-foreground">Perda (%)</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        step={0.1}
                        value={form.wastePercent ?? ""}
                        onChange={(event) =>
                          update("wastePercent", event.target.value ? clamp(parseFloat(event.target.value) || 0) : undefined)
                        }
                        className="tabular-nums pr-8"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">%</span>
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs font-normal text-muted-foreground">Rendimento</Label>
                    <div className="relative">
                      <Input
                        type="number"
                        min={0}
                        max={1}
                        step={0.01}
                        value={form.wastePercent == null ? "" : formatPercent(+(1 - (form.wastePercent ?? 0) / 100).toFixed(4))}
                        onChange={(event) => {
                          const yieldFactor = clamp(parseFloat(event.target.value) || 0, 0, 1);
                          update("wastePercent", event.target.value ? +((1 - yieldFactor) * 100).toFixed(2) : undefined);
                        }}
                        className="tabular-nums pr-12"
                      />
                      <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">×kg</span>
                    </div>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  Ex.: 15% de merma significa que 1kg bruto rende 0,85kg líquido após o preparo.
                </p>
              </div>

              {/* Custos: mesma base da tela "Custos dos produtos". Só em produto já existente (precisa
                  do id) e para quem tem CATALOGO.CUSTO.VISUALIZAR; edição gated por CATALOGO.CUSTO.EDITAR. */}
              {draft?.id && canViewCost && (
                <div className="space-y-2">
                  <Label>Custos</Label>
                  <ProductCostPanel productId={draft.id} canEdit={canEditCost} units={units} />
                </div>
              )}

              <div className="space-y-3 rounded-lg border border-border bg-secondary/30 p-3">
                <div className="flex items-center gap-2">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  <Label className="m-0">Etiqueta & informação nutricional</Label>
                </div>

                <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-background px-3 py-2">
                  <input
                    type="checkbox"
                    checked={!!label.hasBarcode}
                    onChange={(e) => updateLabel("hasBarcode", e.target.checked)}
                    className="h-4 w-4 accent-primary"
                  />
                  <span className="text-sm">Possui código de barras</span>
                </label>

                {label.hasBarcode && (
                  <div className="space-y-1.5">
                    <Label className="text-xs font-normal text-muted-foreground">Número EAN *</Label>
                    <Input
                      type="text"
                      value={label.ean ?? ""}
                      onChange={(e) => updateLabel("ean", e.target.value)}
                      name="ean-produto"
                      autoComplete="one-time-code"
                      data-1p-ignore
                      data-lpignore="true"
                      data-form-type="other"
                    />
                  </div>
                )}

                <div className="grid grid-cols-3 gap-3">
                  <div className="space-y-1.5">
                    <Label className="text-xs font-normal text-muted-foreground">Peso líq. (g)</Label>
                    <Input type="number" min={0} step="any" value={label.netWeightGrams ?? ""} onChange={(e) => updateLabelNumber("netWeightGrams", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-normal text-muted-foreground">Validade (dias)</Label>
                    <Input type="number" min={0} value={label.shelfLifeDays ?? ""} onChange={(e) => updateLabelNumber("shelfLifeDays", e.target.value)} />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs font-normal text-muted-foreground">Porção (g)</Label>
                    <Input type="number" min={0} step="any" value={label.portionSizeGrams ?? ""} onChange={(e) => updateLabelNumber("portionSizeGrams", e.target.value)} />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-normal text-muted-foreground">Conservação</Label>
                  <Input
                    value={label.storageText ?? ""}
                    onChange={(e) => updateLabel("storageText", e.target.value)}
                    placeholder="Manter sob refrigeração de 0-5°C..."
                  />
                </div>

                <div className="space-y-1.5">
                  <Label className="text-xs font-normal text-muted-foreground">Glúten</Label>
                  <Select value={label.glutenStatus} onValueChange={(v) => updateLabel("glutenStatus", v as GlutenStatus)}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="NAO_CONTEM">Não contém glúten</SelectItem>
                      <SelectItem value="CONTEM">Contém glúten</SelectItem>
                      <SelectItem value="PODE_CONTER">Pode conter glúten</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <p className="text-xs font-medium text-muted-foreground">Tabela nutricional (por 100g)</p>
                  <div className="grid grid-cols-2 gap-3">
                    {NUTRITION_FIELDS.map((field) => (
                      <div key={field.key} className="space-y-1.5">
                        <Label className="text-xs font-normal text-muted-foreground">{field.label}</Label>
                        <Input
                          type="number"
                          min={0}
                          step="any"
                          value={(label[field.key] as number | undefined) ?? ""}
                          onChange={(e) => updateLabelNumber(field.key, e.target.value)}
                          className="tabular-nums"
                        />
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="space-y-2">
                <Label className={cn(!isProcessedCategory && "text-muted-foreground")}>Tipos de corte disponíveis</Label>
                {!isProcessedCategory && (
                  <p className="text-xs text-muted-foreground">
                    Cortes só se aplicam a produtos da categoria PROCESSADOS. Produtos in natura não têm corte.
                  </p>
                )}
                <div className={cn("flex flex-wrap gap-2", !isProcessedCategory && "opacity-60")}>
                  {cutOptions.map((cut) => {
                    const active = (form.availableCuts ?? []).includes(cut);
                    return (
                      <Button
                        key={cut}
                        type="button"
                        variant="chip"
                        size="sm"
                        data-active={active}
                        disabled={!isProcessedCategory}
                        onClick={() => toggleCut(cut)}
                      >
                        {cut}
                      </Button>
                    );
                  })}
                </div>
              </div>

              <div className="space-y-2">
                <Label>Segmentos (linhas de mercado)</Label>
                <p className="text-xs text-muted-foreground">
                  O produto entra na Tabela Padrão de cada segmento selecionado. Sem seleção, entra em todos.
                </p>
                <div className="flex flex-wrap gap-2">
                  {(Object.keys(CLIENT_TYPE_LABELS) as ClientType[]).map((segment) => {
                    const active = (form.segments ?? []).includes(segment);
                    return (
                      <Button
                        key={segment}
                        type="button"
                        variant="chip"
                        size="sm"
                        data-active={active}
                        onClick={() => toggleSegment(segment)}
                      >
                        {CLIENT_TYPE_LABELS[segment]}
                      </Button>
                    );
                  })}
                </div>
              </div>

              {(form.segments ?? []).length > 0 && (
                <div className="space-y-3">
                  <Label>Preços por segmento</Label>
                  <p className="text-xs text-muted-foreground">
                    Defina o preço por unidade{isProcessedCategory && (form.availableCuts?.length ?? 0) > 0 ? " e corte" : ""} em
                    cada segmento. Deixe em branco para precificar depois — o produto entra na Tabela Padrão, mas fica
                    oculto na vitrine até ter preço.
                  </p>
                  {(form.segments ?? []).map((segment) => (
                    <div key={segment} className="space-y-2 rounded-lg border border-border bg-secondary/30 p-3">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-sm font-medium">{CLIENT_TYPE_LABELS[segment]}</span>
                        {(form.segments ?? []).length > 1 && (
                          <Button type="button" variant="ghost" size="sm" onClick={() => copyPricesToOthers(segment)}>
                            <Copy className="h-3.5 w-3.5" /> Copiar p/ demais
                          </Button>
                        )}
                      </div>
                      <div className="space-y-1.5">
                        {priceUnits.map((unit) =>
                          priceCuts.map((cut) => {
                            const key = priceKey(segment, unit, cut);
                            const rowLabel = `${unit}${cut ? ` · ${cut}` : ""}`;
                            return (
                              <div key={key} className="flex items-center gap-2">
                                <span className="flex-1 text-xs text-muted-foreground">{rowLabel}</span>
                                <div className="relative w-32">
                                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-xs text-muted-foreground">R$</span>
                                  <Input
                                    type="number"
                                    min={0}
                                    step="0.01"
                                    value={segmentPrices[key] ?? ""}
                                    onChange={(event) => setSegmentPrices((current) => ({ ...current, [key]: event.target.value }))}
                                    className="pl-9 tabular-nums"
                                    aria-label={`Preço ${CLIENT_TYPE_LABELS[segment]} ${rowLabel}`}
                                  />
                                </div>
                              </div>
                            );
                          }),
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Cancelar</Button>
              <Button variant="hero" onClick={() => void submit()}>
                {draft?.id ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                {draft?.id ? "Salvar alterações" : "Criar produto"}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>

    {canManage && (
      <UnitDialog
        open={unitDialog.open}
        mode={unitDialog.mode}
        units={units}
        onClose={() => setUnitDialog((current) => ({ ...current, open: false }))}
        onCreate={handleCreateUnit}
        onUpdate={onUpdateUnit!}
        onFetchUsage={onFetchUnitUsage!}
      />
    )}
    </>
  );
}
